import { uniqBy, flatMap } from 'lodash';
import { DocumentRecord, DocumentRecordData, HexColor, Identifier, Record, Version, DatabaseType } from '../../offline-first';
import moment from 'moment';

export interface Program extends Record {
  type: 'Exercise Program';
  version: Version;
  exercises: Exercise[];
  exercise_categories: Record[];
  equipments: Record[];
  phases: PhaseType[];
  workout_types: WorkoutType[];
  restrict_labels: RestrictLabel[];
  measures: Measure[];
  measure_configurations: MeasureConfiguration[];
}

export interface Exercise extends Record {
  parameters: object;
  notes?: string;
  exercise_categories?: Identifier[];
  resources?: Resource[];
  equipments?: Identifier[];
}

export enum ResourceType {
  Video = 'video',
  Audio = 'audio',
  Image = 'image',
}

export interface Resource extends Record {
  title: string;
  category?: string;
  sub_category?: string;
  collection?: string;
  description?: string;
  thumbnail_url?: string;
  hls_url: string;
  aspect_ration?: string;
  type?: ResourceType;
  asset_id?: string;
}

export interface PhaseType extends Record {
  color: HexColor;
}

export interface WorkoutType extends Record {
  tiers: Tier[];
}

export interface Tier extends Record {
  order: number;
  num_rows: number;
  notes?: string;
  rows: TierRow[];
}

export interface TierRow {
  order: number;
  exercises: Identifier[];
  parameter_overrides: object;
}

export const tierRowParams: string[] = ['sets', 'reps', 'load', 'time', 'distance', 'hold'];
export const getDefaultParams = () => {
  return tierRowParams.map(param => {
    return {
      param,
      unit: '',
      type: 'text',
    };
  });
};

export interface RestrictLabel extends Record {
  notes?: string;
}

export class ExerciseProgramDocument extends DocumentRecord<Program> {
  static recordName = 'ExerciseProgramDocument';
  static objectStoreName = 'exerciseProgramDocuments';

  constructor(data?: DocumentRecordData<Program>) {
    super(data);
  }
}

export interface ParameterOption {
  param: string;
  unit: string;
  type: string;
}

export class ExerciseProgramDocumentConstants {
  static intArrayType = 'int-array';
  static strArrayType = 'str-array';
}

export interface Measure extends Record {
  description: string;
  tags: string[];
  parameters: {
    property: string;
    type: string;
    required: boolean;
    unit?: string;
  }[];
}

export interface MeasureConfiguration extends Record {
  description: string;
  measure_configuration_ids?: Identifier[];
  measure_ids?: Identifier[];
}

export class ExerciseProgramDocumentCache extends ExerciseProgramDocument {
  static recordName = 'ExerciseProgramDocumentCache';
  static objectStoreName = 'exerciseProgramDocumentsCache';
  static fields = ['version', 'document', 'lastSynced'];

  static databaseType = DatabaseType.Document;

  syncedAt: moment.Moment;

  constructor(program: ExerciseProgramDocument) {
    super({ key: program.key, document: program.document, version: program.version });

    this.syncedAt = moment();
    this.createdAt = program.createdAt;
    this.updatedAt = program.updatedAt;
    this.deletedAt = program.deletedAt;
  }
}

export class ExerciseProgramDocumentUtils {
  static getPossibleParamsForExercise(exerciseId: string, programRef: Program) {
    const constants = ExerciseProgramDocumentConstants;
    if (!exerciseId) {
      throw new Error('Invalid exercise id');
    }

    const exercise = programRef.exercises.find(ex => ex.id === exerciseId);
    if (!exercise) {
      throw new Error(`Exercise ${exerciseId} not found`);
    }

    const params = Object.keys(exercise.parameters);
    if (params.length > 0) {
      return params.map((key, value) => {
        return {
          param: key,
          unit: exercise.parameters[key].unit,
          type:
            exercise.parameters[key].type === constants.intArrayType
              ? 'number'
              : exercise.parameters[key].type === constants.strArrayType
              ? 'text'
              : 'text',
        } as ParameterOption;
      });
    } else {
      return getDefaultParams();
    }
  }

  static getPossibleParamsFromTier(tier: Tier, programRef: Program) {
    if (tier) {
      return uniqBy(
        flatMap(tier.rows, row => {
          return flatMap(row.exercises, exerciseId => {
            const exercise = programRef.exercises.find(ex => ex.id === exerciseId);
            return Object.keys(row.parameter_overrides).map((key, value) => {
              // override parameter with tier template values if exist
              const option = {
                param: key,
              } as ParameterOption;

              if (row.parameter_overrides[key].unit) {
                option.unit = row.parameter_overrides[key].unit;
              } else {
                option.unit = exercise.parameters[key] ? exercise.parameters[key].unit : 'N/A';
              }

              if (row.parameter_overrides[key].type) {
                option.type = row.parameter_overrides[key].type;
              } else {
                option.type = exercise.parameters[key]
                  ? exercise.parameters[key].type === 'int-array'
                    ? 'number'
                    : exercise.parameters[key].type === 'str-array'
                    ? 'text'
                    : 'text'
                  : 'text';
              }

              return option;
            });
          });
        }),
        param => param.param,
      );
    } else {
      return getDefaultParams();
    }
  }

  static orderParams(params: string[] | ParameterOption[]) {
    function compareFn(a, b) {
      const aParam = a.param ? a.param : a;
      const bParam = b.param ? b.param : b;

      if (aParam === 'sets') {
        return -1;
      } else if (aParam === 'reps') {
        if (bParam === 'sets') {
          return 1;
        } else {
          return -1;
        }
      } else if (aParam === 'load') {
        if (bParam === 'sets' || bParam === 'reps') {
          return 1;
        } else {
          return -1;
        }
      } else if (bParam === 'sets') {
        return 1;
      } else if (bParam === 'reps') {
        if (aParam === 'sets') {
          return -1;
        } else {
          return 1;
        }
      } else if (bParam === 'load') {
        if (aParam === 'sets' || aParam === 'reps') {
          return -1;
        } else {
          return 1;
        }
      } else {
        return aParam > bParam ? 1 : -1;
      }
    }

    return params.sort(compareFn);
  }
}
